/**
 * Category Form Page
 *
 * Implements: US-M5-003 (Manage Categories)
 * AC-167: Add category with: Name, Description, Banner, Parent (optional)
 * AC-168: Edit category details
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Page,
  Card,
  FormLayout,
  TextField,
  Select,
  Button,
  BlockStack,
  InlineStack,
  Text,
  Spinner,
  Banner,
  Box,
  Thumbnail,
} from '@shopify/polaris';
import { categoryService, type Category, type CreateCategoryInput } from '../../services/category.service';

export function CategoryFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id && id !== 'new';

  // Form state
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [parentId, setParentId] = useState('');
  const [displayOrder, setDisplayOrder] = useState('0');
  const [status, setStatus] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');

  // UI state
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Load categories for parent selector
  useEffect(() => {
    categoryService.list().then(setCategories).catch(console.error);
  }, []);

  // Load category if editing
  useEffect(() => {
    if (isEditing && id) {
      setLoading(true);
      categoryService
        .getById(id)
        .then((category) => {
          setName(category.name);
          setSlug(category.slug);
          setDescription(category.description || '');
          setBannerUrl(category.bannerUrl || '');
          setParentId(category.parentId || '');
          setDisplayOrder(category.displayOrder.toString());
          setStatus(category.status);
        })
        .catch((err) => {
          console.error('Failed to load category:', err);
          setError('Failed to load category');
        })
        .finally(() => setLoading(false));
    }
  }, [isEditing, id]);

  // Auto-generate slug from name
  const handleNameChange = useCallback((value: string) => {
    setName(value);
    if (!isEditing) {
      const newSlug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setSlug(newSlug);
    }
  }, [isEditing]);

  // Validate form
  const validateForm = useCallback((): string | null => {
    if (!name.trim()) return 'Name is required';
    if (!slug.trim()) return 'Slug is required';
    if (!/^[a-z0-9-]+$/.test(slug)) return 'Slug can only contain lowercase letters, numbers, and hyphens';
    return null;
  }, [name, slug]);

  // Submit form
  const handleSubmit = useCallback(async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const categoryData: CreateCategoryInput = {
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim() || undefined,
        bannerUrl: bannerUrl.trim() || undefined,
        parentId: parentId || undefined,
        displayOrder: parseInt(displayOrder) || 0,
        status,
      };

      if (isEditing && id) {
        await categoryService.update(id, categoryData);
      } else {
        await categoryService.create(categoryData);
      }

      navigate('/categories');
    } catch (err: any) {
      console.error('Failed to save category:', err);
      setError(err.response?.data?.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  }, [
    validateForm,
    name,
    slug,
    description,
    bannerUrl,
    parentId,
    displayOrder,
    status,
    isEditing,
    id,
    navigate,
  ]);

  // Parent category options (exclude self and children when editing)
  const getParentOptions = () => {
    const options = [{ label: 'No parent (root category)', value: '' }];

    const addOptions = (cats: Category[], level: number = 0) => {
      for (const cat of cats) {
        // Skip self and children when editing
        if (isEditing && (cat.id === id || cat.parentId === id)) continue;
        // Only allow one level of nesting
        if (level === 0) {
          options.push({
            label: `${'  '.repeat(level)}${cat.name}`,
            value: cat.id,
          });
        }
        if (cat.children) {
          addOptions(cat.children, level + 1);
        }
      }
    };

    addOptions(categories.filter((c) => !c.parentId));
    return options;
  };

  // Status options
  const statusOptions = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Inactive', value: 'INACTIVE' },
  ];

  if (loading) {
    return (
      <Page title="Loading...">
        <Card>
          <Box padding="600">
            <InlineStack align="center">
              <Spinner size="large" />
            </InlineStack>
          </Box>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title={isEditing ? 'Edit Category' : 'Add Category'}
      backAction={{ content: 'Categories', onAction: () => navigate('/categories') }}
      primaryAction={{
        content: 'Save',
        loading: saving,
        onAction: handleSubmit,
      }}
    >
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={() => setError('')}>
            {error}
          </Banner>
        )}

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Category Details
            </Text>
            <FormLayout>
              <TextField
                label="Category Name"
                value={name}
                onChange={handleNameChange}
                autoComplete="off"
                requiredIndicator
              />
              <TextField
                label="Slug"
                value={slug}
                onChange={setSlug}
                autoComplete="off"
                helpText="URL-friendly identifier (lowercase, numbers, hyphens only)"
                requiredIndicator
              />
              <TextField
                label="Description"
                value={description}
                onChange={setDescription}
                multiline={3}
                autoComplete="off"
              />
              <Select
                label="Parent Category"
                options={getParentOptions()}
                value={parentId}
                onChange={setParentId}
                helpText="Leave empty to create a root category"
              />
              <FormLayout.Group>
                <TextField
                  label="Display Order"
                  type="number"
                  value={displayOrder}
                  onChange={setDisplayOrder}
                  autoComplete="off"
                />
                <Select
                  label="Status"
                  options={statusOptions}
                  value={status}
                  onChange={(value) => setStatus(value as 'ACTIVE' | 'INACTIVE')}
                />
              </FormLayout.Group>
            </FormLayout>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Banner Image
            </Text>
            <FormLayout>
              <TextField
                label="Banner Image URL"
                value={bannerUrl}
                onChange={setBannerUrl}
                autoComplete="off"
                placeholder="https://example.com/banner.jpg"
                helpText="Enter a URL for the category banner image"
              />
              {bannerUrl && (
                <Box>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Preview:
                  </Text>
                  <Box paddingBlockStart="200">
                    <Thumbnail source={bannerUrl} alt="Banner preview" size="large" />
                  </Box>
                </Box>
              )}
            </FormLayout>
          </BlockStack>
        </Card>

        {/* Actions */}
        <InlineStack gap="300" align="end">
          <Button onClick={() => navigate('/categories')}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} loading={saving}>
            {isEditing ? 'Save Changes' : 'Create Category'}
          </Button>
        </InlineStack>
      </BlockStack>
    </Page>
  );
}
